import { Moment } from 'moment';
import { Utilities, Pair, } from '@socrates/common';
import { ISearchParam } from './ISearchParam';
import { SortParam } from './SortParam';
import { I18NService } from '../eighteen/I18NService';

export class SearchOptions {
	// name / value map for query parameters to be sent for current search to rest server
	protected _argMap: Map<string, ISearchParam> = new Map<string, ISearchParam>();
	// names to search with standard search term, empty means all fields
	protected _searchFields: Set<string> = new Set<string>();
	// Glenn -- before you go to implement a local name map again, the searchparam and sortparam
	// classes have a localName property.  :-)
	protected _pageSize = 25;
	protected _sortOrder: string[] = [];
	protected _startDate: Moment = null;
	protected _startDateModified = false;
	protected _endDate: Moment = null;
	protected _endDateModified = false;
	protected _searchTerm: string;
	protected _numPages = 1;
	protected _currentPage = 0;
	protected _startDateField: string;
	protected _endDateField: string;
	protected _startDateArgumentName = 'start_date';
	protected _stopDateArgumentName = 'stop_date';
	protected _joinSort: boolean;
	protected _localSortables: Map<string, SortParam> = new Map<string, SortParam>();
	protected _orderByParamName = 'order_by';
	protected _ext: any = { };

	constructor(stringified: any = null) {
		if (stringified) {
			this.setCopy(stringified);
		} else {
			this.resetSearchOptions();
		}
	}

	hasSearch(): boolean {
		let somethingThere = false;
		const argMapCount: number = this._argMap.size;
		const searchFieldsSize: number = this._searchFields.size;

		if ((this._searchTerm && this._searchTerm?.length > 0) || this._startDate || this._endDate || argMapCount > 0 || searchFieldsSize > 0) {
			somethingThere = true;
		}
		return somethingThere;
	}

	getSearchOptions(): any {
		const search: any = {
			results_per_page: this._pageSize * this._numPages || 1,
			num_pages: this.numPagesToPull || 1,
			current_page: this._currentPage
		};

		if (this.sortOrder && this.sortOrder.length > 0) {
			if (this._joinSort) {
				search[this._orderByParamName] = this.sortOrder.join();
			} else {
				search[this._orderByParamName] = this.sortOrder;
			}
		}

		return this.compileSearchExpression(search);
	}

	setValues(pageSize: number, sortFields: string[], startDate: Moment, endDate: Moment, searchTerm: string): void {
		this._pageSize = pageSize;
		this._sortOrder = sortFields;
		if (startDate) {
			this._startDate = Utilities.getUTCDate(startDate);
			this._startDateModified = true;
		}
		if (endDate) {
			this._endDate = Utilities.getUTCDate(endDate);
			this._endDateModified = true;
		}
		this._searchTerm = searchTerm;
	}

	addSortable(sortable: SortParam): void {
		this._localSortables.set(sortable.paramName, sortable);
	}

	setCopy(copy: any): void {
		// copy._argMap may be a serialized plain object (from JSON.parse(JSON.stringify(this)))
		// or a live native Map. Handle both.
		if (copy._argMap) {
			if (copy._argMap instanceof Map) {
				copy._argMap.forEach((value: ISearchParam, key: string) => {
					this._argMap.set(key, value);
				});
			} else if (copy._argMap._map) {
				// Legacy serialized HashMap — iterate the plain backing object
				for (const prop in copy._argMap._map) {
					if (Object.prototype.hasOwnProperty.call(copy._argMap._map, prop)) {
						const mapping: any = copy._argMap._map[prop];
						this._argMap.set(mapping._key, mapping._value);
					}
				}
			}
		}
		if (copy._searchFields) {
			if (copy._searchFields instanceof Set) {
				copy._searchFields.forEach((v: string) => this._searchFields.add(v));
			} else if (copy._searchFields.map?._map) {
				// Legacy serialized HashSet — iterate the inner HashMap backing object
				for (const prop in copy._searchFields.map._map) {
					if (Object.prototype.hasOwnProperty.call(copy._searchFields.map._map, prop)) {
						const mapping: any = copy._searchFields.map._map[prop];
						this._searchFields.add(mapping._value);
					}
				}
			}
		}
		this._pageSize = copy._pageSize;
		this._sortOrder = copy._sortOrder;
		this._startDateModified = copy._startDateModified;
		this._endDateModified = copy._endDateModified;
		this._searchTerm = copy._searchTerm;
		this._numPages = copy._numPages;
		this._currentPage = copy._currentPage;
		this._startDateField = copy._startDateField;
		this._endDateField = copy._endDateField;
		if (copy._startDate) {
			this._startDate = Utilities.getUTCDate(copy._startDate);
		}
		if (copy._endDate) {
			this._endDate = Utilities.getUTCDate(copy._endDate);
		}
		this._startDateArgumentName = copy._startDateArgumentName;
		this._stopDateArgumentName = copy._stopDateArgumentName;
	}

	resetSearchOptions(): void {
		this._pageSize = 25;
		this._currentPage = 1;
		this._sortOrder = [ ];
		this._startDate = null;
		this._startDateModified = false;
		this._endDate = null;
		this._endDateModified = false;
		this._searchTerm = null;
		this._argMap = new Map<string, ISearchParam>();
	}

	clone(): SearchOptions {
		const newMe: SearchOptions = new SearchOptions(JSON.parse(JSON.stringify(this)));
		if (this._startDate) {
			newMe._startDate = this._startDate;
		}

		if (this._endDate) {
			newMe._endDate = this._endDate;
		}
		return newMe;
	}

	get pageSize(): number {
		return this._pageSize;
	}

	set pageSize(newSize: number) {
		this._pageSize = newSize;
	}

	get sortOrder(): string[] {
		return this._sortOrder;
	}

	set sortOrder(newOrder: string[]) {
		this._sortOrder = newOrder;
	}

	get startDate(): Moment {
		return this._startDate;
	}

	set startDate(newStartDate: Moment) {
		if (newStartDate) {
			this._startDateModified = true;
			this._startDate = Utilities.getUTCDate(newStartDate);
		} else {
			this._startDateModified = false;
			this._startDate = null;
		}
	}

	get endDate(): Moment {
		return this._endDate;
	}

	set endDate(newEndDate: Moment) {
		if (newEndDate) {
			this._endDateModified = true;
			this._endDate = Utilities.getUTCDate(newEndDate);
		} else {
			this._endDateModified = false;
			this._endDate = null;
		}
	}

	get searchTerm(): string {
		return this._searchTerm;
	}

	set searchTerm(newSearchTerm: string) {
		this._searchTerm = newSearchTerm;
	}

	get startDateModified(): boolean {
		return this._startDateModified;
	}

	get endDateModified(): boolean {
		return this._endDateModified;
	}

	get numPagesToPull(): number {
		return this._numPages;
	}

	set numPagesToPull(newNumPages: number) {
		this._numPages = newNumPages;
	}

	get currentPage(): number {
		return this._currentPage;
	}

	set currentPage(newCurrentPage: number) {
		this._currentPage = newCurrentPage;
	}

	getEnglishSearchComponents(): string {
		let searchExpression = I18NService.getMessage('searching.emptyExpression');
		const searchComponents: string[] = [];

		if (this.searchTerm && this.searchTerm.length > 0) {
			searchComponents.push(this.searchTerm);
		}

		if (this.startDate) {
			searchComponents.push(I18NService.getMessage('search.startDate', this.startDate.format('YYYY/MM/DD')));
		}

		if (this.endDate) {
			searchComponents.push(I18NService.getMessage('search.endDate', this.endDate.format('YYYY/MM/DD')));
		}

		this._argMap.forEach((val: any, key: string): void => {
			if (typeof val !== 'string') {
				val = val.getParamValue();
			}
			if (searchComponents.length > 0) {
				searchComponents.push(', ');
			}
			searchComponents.push(`${key}=${val}`);
		});

		if (searchComponents.length > 0) {
			searchExpression = Utilities.englishJoinArray(searchComponents);
		}
		return searchExpression;
	}

	clearArgument(argName: string): void {
		this._argMap.delete(argName);
	}

	getArgument(argName: string): any {
		return this._argMap.get(argName);
	}

	setArgument(argName: string, argValue: any): void {
		this._argMap.set(argName, argValue);
	}

	addSearchParam(searchParam: ISearchParam): void {
		this._argMap.set(searchParam.paramName, searchParam);
	}

	getSearchParam(paramName: string): ISearchParam {
		return this._argMap.get(paramName);
	}

	sameSearchExpression(searchOptions: SearchOptions, includeSort = false): boolean {
		let equals = false;

		if (!this.ext?.forceSort && !searchOptions?.ext?.forceSort && searchOptions) {
			if (this.searchTerm === searchOptions.searchTerm &&
				this._endDate === searchOptions._endDate &&
				this._startDate === searchOptions._startDate &&
				Utilities.equals(searchOptions._searchFields, this._searchFields)
			) {
				if (this._argMap.size === searchOptions._argMap.size) {
					equals = true;

					if (includeSort) {
						if ((!this._sortOrder && searchOptions.sortOrder) || (this._sortOrder && !searchOptions.sortOrder)) {
							equals = false;
						} else if (this._sortOrder) {
							if (this._sortOrder.length !== searchOptions.sortOrder.length) {
								equals = false;
							} else {
								for (let jj = 0; jj < this._sortOrder.length; jj++) {
									if (this.sortOrder[jj] !== searchOptions.sortOrder[jj]) {
										equals = false;
										break;
									}
								}
							}
						}
					}

					if (equals && this._argMap) {
						const myArgs = [...this._argMap.values()];
						const hisArgs = [...searchOptions._argMap.values()];
						for (let li = 0; li < this._argMap.size; li++) {
							const myArg: ISearchParam = myArgs[li];
							const hisArg: ISearchParam = hisArgs[li];
							if (!Utilities.equals(myArg, hisArg)) {
								equals = false;
								break;
							}
						}
					}
				}
			}
		}
		return equals;
	}

	sameSearchAndSortExpression(searchOptions): boolean {
		return this.sameSearchExpression(searchOptions, true);
	}

	get searchExpression(): string {
		return JSON.stringify(this.compileSearchExpression({ }));
	}

	get searchTermsAndValues(): Pair<string, any>[] {
		const tvs: Pair<string, any>[] = [ ];

		if (this._searchTerm) {
			if (this._searchFields.size) {
				this._searchFields.forEach(
					(fieldName: string) => {
						if (fieldName.indexOf('|=') >= 0)  {
							const nv: any[] = fieldName.split('|=');
							tvs.push(new Pair<string, any>(nv[0], nv[1]));
						} else {
							tvs.push(new Pair<string, any>(fieldName, this._searchTerm));
						}
					}
				);
			} else {
				tvs.push(new Pair<string, any>('any', this._searchTerm));
			}
		}

		this._argMap.forEach((value: ISearchParam, key: string) => {
			tvs.push(new Pair<string, ISearchParam>(key, value));
		});
		return tvs;
	}

	get searchKeys(): string[] {
		return [...this._argMap.keys()];
	}

	get argMap(): Map<string, ISearchParam> {
		return this._argMap;
	}

	get startDateField(): string {
		return this._startDateField;
	}

	set startDateField(newStartDateField: string) {
		this._startDateField = newStartDateField;
	}

	get endDateField(): string {
		return this._endDateField;
	}

	set endDateField(newEndDateField: string) {
		this._endDateField = newEndDateField;
	}

	get searchFields(): Set<string> {
		return this._searchFields;
	}

	set searchFields(newSearchFields: Set<string>) {
		this._searchFields = newSearchFields;
	}

	addSearchField(field: string): void {
		this._searchFields.add(field);
	}

	clearSearchFields(): void {
		this._searchFields.clear();
	}

	marshallLocalSearchTerms(excludedNames: string[] = []): any[] {

		const marshalledFields: any[] = [];
		const termMap: Map<string, string[]> = new Map<string, string[]>();
		const anyFields: Map<string, string[]> = new Map<string, string[]>();
		const excludes: Set<string> = new Set<string>(excludedNames);

		const searchTerms: Pair<string, any>[] = this.searchTermsAndValues;
		searchTerms?.forEach(
			(pare: Pair<string, any>) => {
				if (!excludes.has(pare.key)) {
					if (pare.key === 'any') {
						let anyFieldArray: string[] = anyFields.get(pare.value);
						if (!anyFieldArray) {
							anyFieldArray = [];
							anyFields.set(pare.value, anyFieldArray);
						}
						anyFieldArray.push(pare.key);
					} else {
						let fieldArray: string[] = termMap.get(pare.value);
						if (!fieldArray) {
							fieldArray = [];
							termMap.set(pare.value, fieldArray);
						}
						fieldArray.push(pare.key);
					}
				}
			}
		);
		termMap.forEach((fields: string[], key: string) => {
			const element: any = {
				searchTerm: key,
				fields,
				isAny: false
			};
			marshalledFields.push(element);
		});
		// want any fields at the end since it is the most expensive (by far)
		anyFields.forEach((_, key: string) => {
			const element: any = {
				searchTerm: key,
				fields: termMap.get(key),
				isAny: true
			};
			marshalledFields.push(element);
		});
		return marshalledFields;
	}

	// Sort fields show up as +fieldName, or -fieldName (desc / asc).  Potential sort fields as SortParam objects
	// can be stored in the member property: _localSortables.  When the name is found in there, the
	// local name will be used. (If there is no local name on the field, the SortParam returns the server
	// name).  If the name isn't found in the _localSortables map, the value in the sortOrder element
	// will be used as is.
	get localFieldsSortOrder(): string[] {
		const sortFields: string[] = [];
		this._sortOrder.forEach(
			(field: string) => {
				const fieldName: string = this.getSortFieldName(field);
				const potentialSortField: SortParam = this._localSortables.get(fieldName);
				if (potentialSortField !== null) {
					sortFields.push(`${field[0]}${potentialSortField.localName}`);
				} else {
					sortFields.push(field);
				}
			}
		);
		return sortFields;
	}

	get joinSort(): boolean {
		return this._joinSort;
	}

	set joinSort(newJoinSort: boolean) {
		this._joinSort = newJoinSort;
	}

	get startDateArgumentName(): string {
		return this._startDateArgumentName;
	}

	set startDateArgumentName(newStartDateArgumentName: string) {
		this._startDateArgumentName = newStartDateArgumentName;
	}

	get stopDateArgumentName(): string {
		return this._stopDateArgumentName;
	}

	set stopDateArgumentName(newStopDateArgumentName: string) {
		this._stopDateArgumentName = newStopDateArgumentName;
	}

	private compileSearchExpression(search: any): any {

		if (this._startDate && this._startDateModified)  {
			search[this.startDateArgumentName] = this._startDate.format('YYYY/MM/DD');
		}

		if (this._endDate && this._endDateModified)  {
			search[this.stopDateArgumentName] = this._endDate.format('YYYY/MM/DD');
		}

		if (this._searchFields && this._searchFields.size) {
			this._searchFields.forEach(
				(fieldName: string) => {
					const field: any = { name: fieldName, value: this._searchTerm };
					if (fieldName.indexOf('|=') >= 0)  {
						const nv: any[] = fieldName.split('|=');
						field.name = nv[0];
						field.value = nv[1];
					}
					if (!search.search_terms) {
						search.search_terms = [];
					}
					search.search_terms.push(field);
				}
			);
		} else if (this.searchTerm && this.searchTerm.length > 0) {
			search.search_term = this._searchTerm;
		}

		this._argMap.forEach((value: any, key: string) => {
			search[key] = value;
		});
		return search;
	}

	private getSortFieldName(sortField: string): string {
		let sortFieldName: string = sortField;

		if (sortFieldName[0] === '+' || sortFieldName[0] === '-') {
			sortFieldName = sortFieldName.substring(1);
		}
		return sortFieldName;
	}

	get ext(): any {
		return this._ext;
	}

	set ext(newValue: any) {
		this._ext = newValue;
	}

}
